import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Evaluator } from '@/types/evaluator';
import { Edit, Trash2, Clock, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { deleteEvaluator } from '@/services/evaluator';
import { toast } from 'react-hot-toast';
import ConfirmationDialog from '../common/ConfirmationDialog';
import EditEvaluatorForm from './EditEvaluatorForm';
import { useEmailCopyModal } from '@/hooks/useEmailCopyModal';

interface EvaluatorListProps {
  evaluators: Evaluator[];
  onEdit: (evaluator: Evaluator) => void;
  onDelete: (evaluatorId: string) => void;
  maxEvaluators: number;
  onUpdate: () => void;
}

export default function EvaluatorList({
  evaluators,
  onEdit,
  onDelete,
  maxEvaluators,
  onUpdate
}: EvaluatorListProps) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [evaluatorToDelete, setEvaluatorToDelete] = useState<Evaluator | null>(null);
  const [evaluatorToEdit, setEvaluatorToEdit] = useState<Evaluator | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { openEmailModal } = useEmailCopyModal();

  const handleProvideContext = (evaluator: Evaluator) => {
    if (!currentUser) return;

    const params = new URLSearchParams({
      evaluatorId: evaluator.id,
      evaluatorFirstName: evaluator.firstName,
      evaluatorLastName: evaluator.lastName,
      evaluatorEmail: evaluator.originalEmail || evaluator.email,
      userEmail: currentUser.email || ''
    });

    navigate(`/provide-context?${params.toString()}`);
  };

  const handleEditClick = (evaluator: Evaluator) => {
    if (evaluator.evaluationCompleted) {
      toast.error("Cannot edit an evaluator who has completed their evaluation");
      return;
    }
    setEvaluatorToEdit(evaluator);
  };

  const handleDeleteClick = (evaluator: Evaluator) => {
    if (evaluator.evaluationCompleted) {
      toast.error("Cannot delete an evaluator who has completed their evaluation");
      return;
    }
    setEvaluatorToDelete(evaluator);
  };

  const handleCopyEmail = (evaluator: Evaluator) => {
    if (currentUser) {
      openEmailModal(evaluator, currentUser.email || '');
    }
  };

  const handleConfirmDelete = async () => {
    if (!evaluatorToDelete || !currentUser || isDeleting) return;

    setIsDeleting(true);
    try {
      await deleteEvaluator(evaluatorToDelete.id, currentUser.uid);
      onDelete(evaluatorToDelete.id);
      toast.success('Evaluator deleted successfully');
    } catch (error: any) {
      console.error('Error deleting evaluator:', error);
      toast.error(error.message || 'Failed to delete evaluator');
    } finally {
      setIsDeleting(false);
      setEvaluatorToDelete(null);
    }
  };

  const handleEditSuccess = () => {
    setEvaluatorToEdit(null);
    onUpdate();
    toast.success('Evaluator updated successfully');
  };

  return (
    <div className="space-y-4">
      {evaluators.map((evaluator) => (
        <motion.div
          key={evaluator.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          {evaluatorToEdit?.id === evaluator.id ? (
            <EditEvaluatorForm
              evaluator={evaluator}
              onSuccess={handleEditSuccess}
              onCancel={() => setEvaluatorToEdit(null)}
            />
          ) : (
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">
                  {evaluator.firstName} {evaluator.lastName}
                </h3>
                <div className="text-gray-600">{evaluator.originalEmail || evaluator.email}</div>
                <div className="flex items-center mt-2 space-x-4">
                  <button
                    onClick={() => handleEditClick(evaluator)}
                    disabled={evaluator.evaluationCompleted}
                    className={`text-gray-600 transition-colors ${
                      evaluator.evaluationCompleted 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:text-gray-900'
                    }`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(evaluator)}
                    disabled={evaluator.evaluationCompleted}
                    className={`transition-colors ${
                      evaluator.evaluationCompleted 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-red-600 hover:text-red-700'
                    }`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-end sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
                {!evaluator.evaluationCompleted && (
                  <button
                    onClick={() => handleCopyEmail(evaluator)}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-dark hover:bg-primary-hover transition-colors"
                  >
                    Generate Email
                  </button>
                )}
                
                <div className={`flex items-center space-x-2 ${
                  evaluator.evaluationCompleted 
                    ? 'text-green-600' 
                    : 'text-amber-600'
                }`}>
                  {evaluator.evaluationCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Clock className="w-5 h-5" />
                  )}
                  <span className="text-sm font-medium">
                    {evaluator.evaluationCompleted ? 'Completed' : 'Pending Response'}
                  </span>
                </div>

                <button
                  onClick={() => handleProvideContext(evaluator)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    evaluator.contextualized
                      ? 'bg-green-50 text-green-600'
                      : 'bg-primary text-dark hover:bg-primary-hover'
                  }`}
                  disabled={evaluator.contextualized}
                >
                  {evaluator.contextualized ? 'Context Provided' : 'Provide Context'}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      ))}

      {evaluators.length === 0 && (
        <div className="text-center py-12 text-gray-600">
          No evaluators nominated yet. Add up to {maxEvaluators} evaluators.
        </div>
      )}

      <ConfirmationDialog
        isOpen={!!evaluatorToDelete}
        title="Delete Evaluator"
        message={evaluatorToDelete ? `Are you sure you want to delete ${evaluatorToDelete.firstName} ${evaluatorToDelete.lastName} from your evaluators list? This action cannot be undone.` : ''}
        onConfirm={handleConfirmDelete}
        onCancel={() => setEvaluatorToDelete(null)}
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        disabled={isDeleting}
      />
    </div>
  );
}